import { useCallback, useRef, useState } from 'react';
import { Hint, Panel } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { runAnalysis } from '@/engine/pipeline';
import { readFileAsDataUrl, formatBytes } from '@/lib/utils';
import { SAMPLE_SCENES, renderSampleScene } from '@/assets/sampleScenes';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export function ImageUploader() {
  const photo = useStore((s) => s.photoDataUrl);
  const photoName = useStore((s) => s.photoName);
  const analysis = useStore((s) => s.analysis);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback(async (file: File) => {
    setError(null);
    if (!ACCEPTED.includes(file.type)) {
      setError(`${file.type || 'That file'} is not a supported image type.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      store.set({
        photoDataUrl: dataUrl,
        photoName: `${file.name} · ${formatBytes(file.size)}`,
        analysis: null,
        variants: [],
        activeId: null,
      });
      await runAnalysis(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const useSample = useCallback(async (id: string, label: string) => {
    setError(null);
    const dataUrl = renderSampleScene(id);
    store.set({
      photoDataUrl: dataUrl,
      photoName: `${label} (synthetic sample)`,
      analysis: null,
      variants: [],
      activeId: null,
    });
    await runAnalysis(dataUrl);
  }, []);

  return (
    <Panel title="Photo">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) void accept(file);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        className={`relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border border-dashed transition ${
          dragging
            ? 'border-accent bg-accent/10'
            : 'border-white/12 bg-white/[0.02] hover:border-white/25'
        } ${photo ? 'aspect-[4/3]' : 'aspect-[4/2.4]'} max-h-[260px]`}
      >
        {photo ? (
          <>
            <img
              src={photo}
              alt="Uploaded preview"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2.5 py-2">
              <p className="truncate text-[10px] text-ink-200">{photoName}</p>
            </div>
          </>
        ) : (
          <div className="px-4 text-center">
            <p className="text-xs text-ink-200">Drop a photo, or click to browse</p>
            <p className="mt-1 text-[10px] text-ink-500">PNG, JPG, WebP or GIF</p>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void accept(file);
          e.target.value = '';
        }}
      />

      {error && <p className="mt-2 text-[10px] text-red-400">{error}</p>}

      {analysis && (
        <div className="mt-2.5 flex flex-wrap gap-1">
          <span className="chip">
            {analysis.width}×{analysis.height}
          </span>
          <span className="chip">
            {analysis.busy ? 'busy' : 'clean'} background
          </span>
          {analysis.faces.length > 0 && (
            <span className="chip">
              {analysis.faces.length} face{analysis.faces.length > 1 ? 's' : ''} ·{' '}
              {analysis.faces[0].source}
            </span>
          )}
          <span className="chip">best: {analysis.zones[0]?.label.toLowerCase()}</span>
        </div>
      )}

      <div className="mt-3">
        <p className="field-label">Sample scenes</p>
        <div className="grid grid-cols-2 gap-1.5">
          {SAMPLE_SCENES.map((scene) => (
            <button
              key={scene.id}
              type="button"
              title={scene.note}
              onClick={() => void useSample(scene.id, scene.label)}
              className="rounded-md bg-white/[0.03] px-2 py-1.5 text-left text-[10px] leading-tight text-ink-300 transition hover:bg-white/[0.08]"
            >
              {scene.label}
            </button>
          ))}
        </div>
        <Hint>
          Synthetic, not photographs — each one is built to exercise a different
          branch of the analyzer so you can test the design system before
          uploading real work.
        </Hint>
      </div>
    </Panel>
  );
}
